import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Package, LogOut, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const AccountPage = () => {
  const { user, login, register, logout, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (isLogin) {
      const result = login(form.email, form.password);
      if (!result.success) setError(result.error);
      else toast({ title: 'Welcome back!', description: `Logged in as ${result.user.name}` });
    } else {
      if (!form.name.trim()) { setError('Name is required'); return; }
      const result = register(form.name, form.email, form.password);
      if (!result.success) setError(result.error);
      else toast({ title: 'Account created!', description: 'Welcome to Luxo-Market' });
    }
  };

  const orders = JSON.parse(localStorage.getItem('passlux_orders') || '[]')
    .filter(o => user && o.customerEmail === user.email);

  const inputCls = "w-full bg-[#111] border border-gray-800 rounded-lg px-4 py-3 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-[#2EC866]/50 transition-colors";

  if (!isAuthenticated) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-[#111] border border-gray-800/50 rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#2EC866]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-[#2EC866]" />
              </div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h1>
              <p className="text-gray-500 text-sm mt-2">
                {isLogin ? 'Sign in to your account' : 'Join the Luxo-Market community'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      className={`${inputCls} pl-10`}
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="text-gray-400 text-xs mb-1.5 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    required
                    className={`${inputCls} pl-10`}
                  />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1.5 block">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={form.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                    minLength={4}
                    className={`${inputCls} pl-10 pr-10`}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button type="submit"
                className="w-full bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold py-3.5 rounded-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(46,200,102,0.2)]"
              >
                {isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <p className="text-center text-gray-500 text-sm mt-6">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-[#2EC866] hover:underline font-medium"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>My Account</h1>
            <p className="text-gray-500 text-sm mt-1">Welcome, {user.name}</p>
          </div>
          <button onClick={logout}
            className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>

        {/* Account Info */}
        <div className="bg-[#111] border border-gray-800/50 rounded-xl p-6 mb-8">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-[#2EC866]" /> Account Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-500 text-xs mb-1">Name</p>
              <p className="text-white text-sm">{user.name}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Email</p>
              <p className="text-white text-sm">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Orders */}
        <div className="bg-[#111] border border-gray-800/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-[#2EC866]" /> Order History
          </h3>
          {orders.length === 0 ? (
            <p className="text-gray-500 text-sm">No orders yet.</p>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-800/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-mono text-sm">{order.id}</span>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      order.status === 'delivered' ? 'bg-green-500/10 text-green-400' :
                      order.status === 'shipped' ? 'bg-blue-500/10 text-blue-400' :
                      'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs mb-2">{order.date}</p>
                  <div className="text-gray-400 text-xs space-y-1">
                    {order.items.map((item, i) => (
                      <p key={i}>{item.name} x{item.quantity}</p>
                    ))}
                  </div>
                  <p className="text-white font-bold text-sm mt-2">&pound;{order.total.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
